import React, { useEffect, useRef, ReactNode, ReactPortal } from 'react';
import ReactDOM from 'react-dom';
import { Divider, ListItemIcon } from '@mui/material';
import { styled } from '@mui/system';

export interface MenuOption {
  label?: string;
  prependTitle?: string;
  onClick?: (event: React.MouseEvent<Element, MouseEvent>) => void;
  icon?: React.ReactNode;
  isDivider?: boolean;
  nonClickable?: boolean;
  style?: React.CSSProperties;
  dataCy?: string;
}

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  options: MenuOption[];
  isIgnoreErrorActive: boolean;
}

const DivStyled = styled('div')(() => ({
  backgroundColor: '#ffffff',
  borderRadius: '4px',
  boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
  maxWidth: '230px',
  padding: '4px 0',
  position: 'absolute',
  zIndex: 9999,
}));

const UlStyled = styled('ul')({
  listStyle: 'none',
  margin: 0,
  padding: '0 6px',
});

const LiStyled = styled('li')<{ nonClickable?: boolean }>(({ nonClickable }) => ({
  alignItems: 'center',
  cursor: nonClickable ? 'default' : 'pointer',
  display: 'flex',
  fontFamily: 'Roboto',
  fontSize: '0.8rem',
  justifyContent: 'flex-start',
  overflow: 'hidden',
  padding: '6px 8px 6px 6px',
  textWrap: 'wrap',
  '&:hover': {
    backgroundColor: nonClickable ? '#ffffff' : '#f0f0f0',
  },
  '& strong': {
    color: nonClickable ? 'rgba(0, 0, 0, 0.75)' : 'inherit',
    '& + span': {
      color: nonClickable ? 'rgba(0, 0, 0, 0.65)' : 'inherit',
    },
  },
}));

const ListItemIconStyled = styled(ListItemIcon)({
  marginLeft: '8px',
  minWidth: '24px',
  '& svg': {
    height: '20px',
  },
});

// FIXME: Do we need to fix this?
//@ts-ignore
const ContextMenu: React.FC<ContextMenuProps> = ({
  x,
  y,
  onClose,
  options,
  isIgnoreErrorActive,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Close menu when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Close menu when 'ESC' key is pressed
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Event listeners for close handling
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup to avoid memory leaks on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Function to prevent the menu from closing when clicking on a non-clickable item
  const handleMenuItemClick = (option: MenuOption) => async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Prevents closing if the item is clickable and has an event handler
    if (option.onClick) {
      event.stopPropagation(); // Stops the event from bubbling up to parent elements
      await option.onClick(event);
      onClose();
    }
  };

  // Using ReactDOM.createPortal to render ContextMenu in the 'body' element, ensuring it's not obscured
  // by parent elements' CSS (like z-index or overflow). This is crucial for floating UI elements like
  // context menus to appear consistently and unclipped in the UI.
  return ReactDOM.createPortal(
    <DivStyled ref={menuRef} style={{ top: y, left: x }}>
      <UlStyled>
        {options.map((option, index) =>
          option.isDivider ? (
            <Divider key={index} />
          ) : (
            <LiStyled
              data-cy={option.dataCy || `ctx-menu-item-${option.label}`}
              key={index}
              onClick={handleMenuItemClick(option)}
              nonClickable={option.nonClickable}
              style={option.style}
            >
              <span>
                {option.prependTitle && <strong>{option.prependTitle}:&nbsp;</strong>}
                <span>{option.label}</span>
              </span>
              {option.icon && (
                <ListItemIconStyled className={isIgnoreErrorActive ? 'is-IgnoreError' : ''}>
                  {option.icon}
                </ListItemIconStyled>
              )}
            </LiStyled>
          )
        )}
      </UlStyled>
    </DivStyled>,
    document.body
  );
};

export default ContextMenu;
